// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from "react";
import SweetAlert from "react-bootstrap-sweetalert";
import styles from "./ErrorHandler.module.scss";
import { errorState } from "recoil/error/atom";
import { useSetRecoilState } from "recoil";

const AUTO_DISMISS_MS = 5000;

const ToastNotification = ({ error, onDismiss }: { error: any; onDismiss: () => void }) => {
  const [fadingOut, setFadingOut] = useState(false);
  const timerRef = useRef<any>(null);

  const getErrorMessageDisplay = (errorDetail: any) => {
    const errors: any[] = [];
    let idx = 0;
    for (const property in errorDetail) {
      if (Array.isArray(errorDetail[property])) {
        errorDetail[property].map((message: string, i: number) =>
          errors.push(<p key={idx + "_" + i}>{message}</p>),
        );
      } else if (typeof errorDetail[property] !== "string") {
        errors.push(<p key={idx}>{JSON.stringify(errorDetail[property])}</p>);
      } else {
        errors.push(<p key={idx}>{errorDetail[property]}</p>);
      }
      idx++;
    }
    return errors;
  };

  const handleDismiss = useCallback(() => {
    setFadingOut(true);
    setTimeout(() => {
      onDismiss();
    }, 300);
  }, [onDismiss]);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      handleDismiss();
    }, AUTO_DISMISS_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [handleDismiss]);

  return (
    <div className={`${styles.toastNotification} ${fadingOut ? styles.toastFadeOut : ""}`}>
      <div className={styles.toastIcon}>
        <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
      </div>
      <div className={styles.toastContent}>
        {error?.title && <div className={styles.toastTitle}>{error.title}</div>}
        <div className={styles.toastMessage}>
          <span>{error.message}</span>
          {error.detail ? <small>{getErrorMessageDisplay(error.detail)}</small> : null}
        </div>
      </div>
      <button className={styles.toastClose} onClick={handleDismiss} aria-label="Close">
        ×
      </button>
      <div className={styles.toastProgressBar} />
    </div>
  );
};

const ErrorHandler = (props: any) => {
  const [error, setError] = useState<any>(null);
  const setErrorState = useSetRecoilState(errorState);

  const hideAlert = (callback: any) => {
    if (callback) {
      callback();
    }
    setErrorState(() => []);
    setError(null);
  };

  const dismissToast = () => {
    if (error?.onConfirm) {
      error.onConfirm();
    } else if (error?.onCancel) {
      error.onCancel();
    }
    setErrorState(() => []);
    setError(null);
  };

  const getErrorMessageDisplay = (errorDetail: any) => {
    const errors: any[] = [];
    let idx = 0;
    for (const property in errorDetail) {
      if (Array.isArray(errorDetail[property])) {
        errorDetail[property].map((message: string, i: number) =>
          errors.push(<p key={idx + "_" + i}>{message}</p>),
        );
      } else if (typeof errorDetail[property] !== "string") {
        errors.push(<p key={idx}>{JSON.stringify(errorDetail[property])}</p>);
      } else {
        errors.push(<p key={idx}>{errorDetail[property]}</p>);
      }
      idx++;
    }
    return errors;
  };

  useEffect(() => {
    const errorIdx = props.errors.length - 1;
    setError(props.errors.length ? props.errors[errorIdx] : null);
  }, [props]);

  if (!error) return null;

  // If error has showCancel, it's a confirmation dialog — keep as SweetAlert modal
  if (error?.showCancel) {
    return (
      <div className={styles.sweetAlert}>
        <SweetAlert
          type={error.type}
          title={error.title}
          confirmBtnCssClass={error.confirmBtnCssClass}
          confirmBtnBsStyle={error.confirmBtnBsStyle || "primary"}
          confirmBtnText={error.confirmBtnText || "OK"}
          onConfirm={() => hideAlert(error?.onConfirm)}
          showCancel={error.showCancel || false}
          cancelBtnBsStyle={error.cancelBtnBsStyle || "outline-secondary"}
          cancelBtnText={error.cancelBtnText || "Cancel"}
          onCancel={() => hideAlert(error?.onCancel)}
        >
          <span>
            <span>{error.message}</span>
            {error.detail ? (
              <small>
                <br />
                {getErrorMessageDisplay(error.detail)}
              </small>
            ) : null}
          </span>
        </SweetAlert>
      </div>
    );
  }

  // Simple error notification — show as toast in upper-right corner
  return (
    <div className={styles.toastContainer}>
      <ToastNotification error={error} onDismiss={dismissToast} />
    </div>
  );
};

export default ErrorHandler;
